import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";
import MediaLogo from "../../assets/MEDIA2-cropped.png";
import "./AdvertisingRequestModal.css";

// Purpose-built 5-screen advertising-request form (Hims-style: one
// question per screen, segmented progress bar, big clickable choice
// cards, fade/slide between screens). Distinct from the generic
// ContactUsModal used elsewhere on the site.
const TOTAL_STEPS = 5;
const CITY_KEYS = ["brossard", "valleyfield", "beauharnois", "chateauguay", "all"];

const emptyForm = {
  name: "",
  title: "",
  email: "",
  phone: "",
  company: "",
  budget: "",
  duration: "",
  cities: [],
  heardFrom: "",
};

const AdvertisingRequestModal = ({ show, handleClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState("forward");
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => /^\d{10}$/.test(phone);

  const update = (field, value) =>
    setFormData((f) => ({ ...f, [field]: value }));

  const toggleCity = (city) => {
    setFormData((f) => {
      if (city === "all") {
        return { ...f, cities: f.cities.includes("all") ? [] : ["all"] };
      }
      const withoutAll = f.cities.filter((c) => c !== "all");
      const has = withoutAll.includes(city);
      return {
        ...f,
        cities: has
          ? withoutAll.filter((c) => c !== city)
          : [...withoutAll, city],
      };
    });
  };

  const canAdvance = () => {
    switch (step) {
      case 0:
        return formData.name.trim() && formData.title.trim();
      case 1:
        return isValidEmail(formData.email) && isValidPhone(formData.phone);
      case 2:
        return formData.company.trim();
      case 3:
        return (
          formData.budget.trim() && formData.duration.trim() && formData.cities.length > 0
        );
      case 4:
        return !!formData.heardFrom;
      default:
        return false;
    }
  };

  const resetAndClose = () => {
    handleClose();
    setStep(0);
    setSuccess(false);
    setError(false);
    setFormData(emptyForm);
  };

  const goNext = () => {
    if (!canAdvance() || step >= TOTAL_STEPS - 1) return;
    setDirection("forward");
    setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step <= 0) return;
    setDirection("back");
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!canAdvance()) return;
    setLoading(true);
    setError(false);
    setSuccess(false);

    try {
      await fetch(process.env.REACT_APP_GOOGLE_SHEET_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          phone: formData.phone.replace(/\D/g, ""),
          cities: formData.cities.join(", "),
          formType: "advertising_request",
        }),
      });

      setSuccess(true);
      setLoading(false);
      setTimeout(resetAndClose, 1400);
    } catch {
      setLoading(false);
      setError(true);
    }
  };

  const steps = t("outOfHomePage.advertisingForm.steps", { returnObjects: true });
  const s = steps[step];

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <>
            <Form.Group className="w-100 mb-3">
              <Form.Label>{s.name.label}</Form.Label>
              <Form.Control
                type="text"
                autoFocus
                value={formData.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder={s.name.placeholder}
                className="arm-input"
              />
            </Form.Group>
            <Form.Group className="w-100">
              <Form.Label>{s.title.label}</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder={s.title.placeholder}
                className="arm-input"
              />
            </Form.Group>
          </>
        );
      case 1:
        return (
          <>
            <Form.Group className="w-100 mb-3">
              <Form.Label>{s.email.label}</Form.Label>
              <Form.Control
                type="email"
                autoFocus
                value={formData.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder={s.email.placeholder}
                className="arm-input"
              />
            </Form.Group>
            <Form.Group className="w-100">
              <Form.Label>{s.phone.label}</Form.Label>
              <Form.Control
                type="text"
                maxLength={10}
                value={formData.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder={s.phone.placeholder}
                className="arm-input"
              />
            </Form.Group>
          </>
        );
      case 2:
        return (
          <Form.Group className="w-100">
            <Form.Label>{s.company.label}</Form.Label>
            <Form.Control
              type="text"
              autoFocus
              value={formData.company}
              onChange={(e) => update("company", e.target.value)}
              placeholder={s.company.placeholder}
              className="arm-input"
            />
          </Form.Group>
        );
      case 3:
        return (
          <div className="d-flex flex-column w-100" style={{ gap: "24px" }}>
            <Form.Group>
              <Form.Label>{s.budget.label}</Form.Label>
              <Form.Control
                type="text"
                value={formData.budget}
                onChange={(e) => update("budget", e.target.value)}
                placeholder={s.budget.placeholder}
                className="arm-input"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>{s.duration.label}</Form.Label>
              <Form.Control
                type="text"
                value={formData.duration}
                onChange={(e) => update("duration", e.target.value)}
                placeholder={s.duration.placeholder}
                className="arm-input"
              />
            </Form.Group>

            <div>
              <Form.Label>{s.cities.label}</Form.Label>
              <div className="arm-choice-grid">
                {CITY_KEYS.map((key) => (
                  <button
                    type="button"
                    key={key}
                    className={`arm-choice-card ${
                      formData.cities.includes(key) ? "selected" : ""
                    }`}
                    onClick={() => toggleCity(key)}
                  >
                    {s.cities.options[key]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="arm-choice-grid">
            {s.options.map((opt) => (
              <button
                type="button"
                key={opt}
                className={`arm-choice-card ${
                  formData.heardFrom === opt ? "selected" : ""
                }`}
                onClick={() => update("heardFrom", opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <Modal
      show={show}
      onHide={resetAndClose}
      fullscreen="sm-down"
      centered
      size="lg"
    >
      {/* Close Button */}
      <Button
        variant="light"
        className="position-absolute top-3 end-3 p-2"
        onClick={resetAndClose}
        style={{ zIndex: 1051 }}
      >
        ✕
      </Button>

      <div className="bg-white rounded-4 d-flex flex-column arm-modal">
        {/* Header: logo + progress bar */}
        <div className="arm-header">
          <img src={MediaLogo} alt="DRINQ Média" className="arm-logo" />
          <div className="arm-progress">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`arm-progress-seg ${i <= step ? "filled" : ""}`}
              />
            ))}
          </div>
        </div>

        {/* Screen content */}
        <div
          key={step}
          className={`arm-screen ${direction === "back" ? "back" : ""}`}
        >
          <h3 className="arm-headline">{s.headline}</h3>
          <div className="arm-fields">{renderStepContent()}</div>
        </div>

        {success && (
          <div className="text-center text-success mt-2">
            {t("outOfHomePage.advertisingForm.success")}
          </div>
        )}
        {error && (
          <div className="text-center text-danger mt-2">
            {t("outOfHomePage.advertisingForm.error")}
          </div>
        )}

        {/* Footer nav */}
        <div className="arm-footer">
          {step > 0 ? (
            <button type="button" className="arm-back-btn" onClick={goBack}>
              <FaArrowLeft size={14} />
              {t("outOfHomePage.advertisingForm.back")}
            </button>
          ) : (
            <span />
          )}

          {isLastStep ? (
            <Button
              variant="dark"
              className="rounded-pill px-4 py-2 arm-cta"
              disabled={!canAdvance() || loading}
              onClick={handleSubmit}
            >
              {loading
                ? t("outOfHomePage.advertisingForm.sending")
                : t("outOfHomePage.advertisingForm.submit")}
            </Button>
          ) : (
            <Button
              variant="dark"
              className="rounded-pill px-4 py-2 arm-cta"
              disabled={!canAdvance()}
              onClick={goNext}
            >
              {t("outOfHomePage.advertisingForm.next")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AdvertisingRequestModal;
